import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { useReveal } from "@/hooks/use-reveal";
import { getTerm, categoryLabel, termsByCategory } from "@/content/glossary";
import { linkifyParagraph, relatedTerms } from "@/lib/glossary-links";


export const Route = createFileRoute("/glossary/$slug")({
  loader: ({ params }) => {
    const term = getTerm(params.slug);
    if (!term) throw notFound();
    return { term };
  },
  head: ({ params, loaderData }) => {
    const term = loaderData?.term;
    if (!term) {
      return {
        meta: [
          { title: "Term not found — The Founders App" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const url = `https://thefoundersapp.com/glossary/${params.slug}`;
    return {
      meta: [
        { title: term.metaTitle },
        { name: "description", content: term.metaDescription },
        { property: "og:title", content: term.metaTitle },
        { property: "og:description", content: term.metaDescription },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { property: "og:image", content: "https://thefoundersapp.com/og-cover.jpg" },
        { name: "twitter:image", content: "https://thefoundersapp.com/og-cover.jpg" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "DefinedTerm",
            name: term.term,
            description: term.definition,
            url,
            inDefinedTermSet: {
              "@type": "DefinedTermSet",
              name: "The Founders App business glossary",
              url: "https://thefoundersapp.com/glossary",
            },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: `What is ${term.term.toLowerCase()}?`,
                acceptedAnswer: { "@type": "Answer", text: term.definition },
              },
              ...(term.body[0]
                ? [
                    {
                      "@type": "Question",
                      name: `Why does ${term.term.toLowerCase()} matter for founders?`,
                      acceptedAnswer: { "@type": "Answer", text: term.body[0] },
                    },
                  ]
                : []),
            ],
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            url,
            name: term.metaTitle,
            speakable: {
              "@type": "SpeakableSpecification",
              cssSelector: ["h1", "[data-answer]"],
            },
          }),
        },

        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "/" },
              { "@type": "ListItem", position: 2, name: "Glossary", item: "/glossary" },
              { "@type": "ListItem", position: 3, name: term.term, item: url },
            ],
          }),
        },
      ],
    };
  },
  component: GlossaryEntry,
  notFoundComponent: TermNotFound,
});

function TermNotFound() {
  return (
    <div className="relative min-h-screen overflow-hidden">
      <div className="aurora" aria-hidden />
      <SiteHeader />
      <main id="main" className="relative mx-auto w-full max-w-3xl px-6 pt-24 pb-24 sm:px-10">
        <h1 className="etched text-3xl font-semibold">That term isn't here.</h1>
        <p className="mt-4 text-sm text-muted-foreground">
          It may have moved. The rest of the glossary is still on the index.
        </p>
        <Link to="/glossary" className="micro-label mt-8 inline-flex items-center gap-2">
          <ArrowLeft className="h-3.5 w-3.5" /> All terms
        </Link>
      </main>
      <SiteFooter />
    </div>
  );
}

function GlossaryEntry() {
  useReveal();
  const { term } = Route.useLoaderData();
  const related = relatedTerms(term);
  const relatedSlugs = new Set(related.map((t) => t.slug));
  const siblings = termsByCategory(term.category)
    .filter((t) => t.slug !== term.slug && !relatedSlugs.has(t.slug))
    .slice(0, 4);
  const used = new Set<string>();

  return (
    <div className="relative min-h-screen overflow-hidden">
      <div className="aurora" aria-hidden />
      <SiteHeader />

      <main id="main" className="relative mx-auto w-full max-w-3xl px-6 pt-16 pb-24 sm:px-10">
        <nav aria-label="Breadcrumb" className="reveal">
          <Link
            to="/glossary"
            className="micro-label inline-flex items-center gap-2 transition-colors hover:text-foreground"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Glossary
          </Link>
        </nav>

        <p className="reveal micro-label mt-8 [--d:60ms]">{categoryLabel(term.category)}</p>

        <h1 className="reveal etched mt-5 text-[2.1rem] leading-[1.02] font-semibold sm:text-5xl [--d:100ms]">
          {term.term}
        </h1>

        <p
          data-answer
          className="reveal mt-6 text-[1.05rem] leading-relaxed text-foreground/90 [--d:160ms]"
        >
          {term.definition}
        </p>


        <div className="hairline scan-rule mt-12" />

        <article className="mt-10 space-y-6">
          {term.body.map((paragraph, i) => (
            <p
              key={`p-${i}`}
              className="reveal text-[0.95rem] leading-relaxed text-muted-foreground"
            >
              {linkifyParagraph(paragraph, term.slug, used)}
            </p>
          ))}
        </article>

        {related.length > 0 && (
          <section className="mt-12">
            <p className="reveal micro-label">Related terms</p>
            <ul className="reveal mt-4 flex flex-wrap gap-2">
              {related.map((t) => (
                <li key={t.slug}>
                  <Link
                    to="/glossary/$slug"
                    params={{ slug: t.slug }}
                    className="inline-flex rounded-sm border border-border px-3 py-2 text-sm transition-colors hover:border-foreground/40 hover:text-foreground"
                  >
                    {t.term}
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}


        <div className="hairline mt-14" />

        <section className="reveal soft-panel mt-8 p-8">
          <p className="micro-label">Try The Founders App</p>
          <h2 className="mt-4 text-xl font-medium">Knowing the term is the easy half.</h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            The Founders App is a PIN-locked record for every business you run: one move a day, one
            bold move a month, and a private timeline of what actually happened.
          </p>
          <Link
            to="/pricing"
            className="micro-label mt-6 inline-flex items-center gap-2 rounded-sm border border-border px-4 py-2.5 transition-colors hover:border-foreground/40 hover:text-foreground"
          >
            See plans <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </section>

        {siblings.length > 0 && (
          <section className="mt-14">
            <p className="reveal micro-label">More in {categoryLabel(term.category)}</p>
            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              {siblings.map((t) => (
                <Link
                  key={t.slug}
                  to="/glossary/$slug"
                  params={{ slug: t.slug }}
                  className="reveal soft-panel block p-5 transition-colors hover:border-foreground/25"
                >
                  <span className="block text-base font-medium">{t.term}</span>
                  <span className="mt-2 block text-sm leading-relaxed text-muted-foreground">
                    {t.definition}
                  </span>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>

      <SiteFooter />
    </div>
  );
}
